import { useState } from "react";
import { Download, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useDiary } from "../../lib/diary";

const TYPES = ["Riego", "Riego con abono", "Observación", "Poda", "Trasplante", "Cosecha"];
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { date: today(), type: "Riego con abono", liters: "", ec: "", ph: "", height: "", note: "" };

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs text-[#C9D6C2]">{label}</span>{children}</label>
);
const inputCls = "mt-1 w-full h-11 rounded-[6px] bg-[#143823] border border-[#F6EEDC]/25 px-3 text-sm text-[#F6EEDC] [color-scheme:dark] focus:border-[#F2B441] outline-none";

const exportCsv = (rows) => {
  const head = ["Fecha", "Semana", "Tipo", "Litros", "EC", "pH", "Altura cm", "Nota"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [head, ...rows.map((r) => [r.date, r.week, r.type, r.liters, r.ec, r.ph, r.height, r.note])].map((l) => l.map(esc).join(";")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv" }));
  a.download = "diario-cultivo-jungle.csv";
  a.click();
};

export const JDiary = ({ weekFor }) => {
  const [rows, setRows] = useDiary();
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = (e) => {
    e.preventDefault();
    setRows((cur) => [{ ...f, id: Date.now(), week: weekFor(f.date) }, ...cur]);
    setF({ ...EMPTY, date: f.date, type: f.type });
    toast.success("Entrada guardada en tu diario");
  };
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <form onSubmit={add} data-testid="diary-form" className="lg:col-span-5 rounded-[10px] label-frame bg-[#0E2A1C] p-5 md:p-7 grid grid-cols-2 gap-4 content-start">
        <Field label="Fecha"><input type="date" required value={f.date} onChange={set("date")} data-testid="diary-date" className={inputCls} /></Field>
        <Field label="Tipo">
          <select value={f.type} onChange={set("type")} data-testid="diary-type" className={inputCls}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        </Field>
        <Field label="Litros"><input type="number" min="0" step="0.1" inputMode="decimal" value={f.liters} onChange={set("liters")} data-testid="diary-liters" className={inputCls} /></Field>
        <Field label="Altura (cm)"><input type="number" min="0" step="1" inputMode="numeric" value={f.height} onChange={set("height")} data-testid="diary-height" className={inputCls} /></Field>
        <Field label="EC (mS/cm)"><input type="number" min="0" step="0.1" inputMode="decimal" value={f.ec} onChange={set("ec")} data-testid="diary-ec" className={inputCls} /></Field>
        <Field label="pH"><input type="number" min="0" max="14" step="0.1" inputMode="decimal" value={f.ph} onChange={set("ph")} data-testid="diary-ph" className={inputCls} /></Field>
        <div className="col-span-2"><Field label="Notas"><textarea rows={3} value={f.note} onChange={set("note")} data-testid="diary-note" placeholder="Color de hojas, olor, plagas…" className={`${inputCls} h-auto py-2`} /></Field></div>
        <button type="submit" data-testid="diary-add" className="col-span-2 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#F2B441] text-[#0E2A1C] font-bold text-sm hover:bg-[#F6EEDC] transition-colors">
          <Plus className="w-4 h-4" /> Guardar entrada
        </button>
        <p className="col-span-2 text-[11px] text-[#C9D6C2]">Tu diario se guarda solo en este navegador.</p>
      </form>
      <DiaryList rows={rows} onDelete={(id) => setRows((cur) => cur.filter((r) => r.id !== id))} />
    </div>
  );
};

const DiaryList = ({ rows, onDelete }) => (
  <div className="lg:col-span-7">
    <div className="flex items-center justify-between mb-3">
      <p className="font-mono-d text-[11px] tracking-[0.2em] uppercase text-[#F2B441]" data-testid="diary-count">{rows.length} entradas</p>
      {rows.length > 0 && (
        <button onClick={() => exportCsv(rows)} data-testid="diary-export" className="inline-flex items-center gap-1.5 text-xs font-semibold hover:text-[#F2B441] transition-colors">
          <Download className="w-3.5 h-3.5" /> Exportar CSV
        </button>
      )}
    </div>
    {rows.length === 0 ? (
      <p data-testid="diary-empty" className="rounded-[10px] border border-dashed border-[#F6EEDC]/20 p-8 text-center text-sm text-[#C9D6C2]">Aún no hay entradas. Apunta tu primer riego.</p>
    ) : (
      <ul data-testid="diary-list" data-lenis-prevent className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
        {rows.map((r) => (
          <li key={r.id} data-testid={`diary-entry-${r.id}`} className="rounded-[8px] bg-[#0E2A1C] border border-[#F6EEDC]/10 p-3.5 flex gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm"><span className="font-bold">{r.type}</span> <span className="text-[#C9D6C2] capitalize">· {new Date(r.date + "T12:00").toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" })}</span>
                {r.week && <span className="ml-2 font-mono-d text-[10px] text-[#8CD05A] uppercase">{r.week}</span>}</p>
              <p className="font-mono-d text-xs text-[#F2B441] mt-1">
                {[r.liters && `${r.liters} L`, r.ec && `EC ${r.ec}`, r.ph && `pH ${r.ph}`, r.height && `${r.height} cm`].filter(Boolean).join(" · ") || "—"}
              </p>
              {r.note && <p className="text-xs text-[#E3EADF] mt-1 break-words">{r.note}</p>}
            </div>
            <button onClick={() => onDelete(r.id)} aria-label="Borrar entrada" data-testid={`diary-delete-${r.id}`} className="shrink-0 w-8 h-8 grid place-items-center rounded-full hover:bg-[#F6EEDC]/10 text-[#C9D6C2] hover:text-[#F2B441] transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);
